import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Moon, Sun, Sparkles, LogOut, Menu, X, Search } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import NotificationBell from "@/components/NotificationBell";
import { SmartSearch } from "@/components/SmartSearch";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { isPremiumUser } from "@/components/UpgradeBanner";
import { SidebarNav } from "@/components/SidebarNav";

export default function Header() {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const isAdmin = user?.is_admin === true;

  // Primary links shown inline. Less-used items go in the More dropdown.
  const primary = [
    { to: "/", key: "home", label: t("nav.home") },
    { to: "/pricing", key: "pricing", label: t("nav.pricing") },
    ...(!user ? [
      { to: "/free-roadmap", key: "free roadmap", label: t("nav.free_roadmap") },
      { to: "/assistants", key: "assistants", label: t("nav.assistants") },
      { to: "/about", key: "about", label: t("nav.about") },
    ] : []),
    ...(user ? [
      { to: "/dashboard", key: "dashboard", label: t("nav.dashboard") },
      { to: "/truffl", key: "truffl", label: t("nav.truffl") },
      { to: "/studio", key: "studio", label: t("nav.studio") },
    ] : []),
    ...(isAdmin ? [{ to: "/admin", key: "admin", label: t("nav.admin") }] : []),
  ];
  const more = user ? [
    { to: "/rewards", key: "rewards", label: t("nav.rewards") },
    { to: "/referrals", key: "referrals", label: t("nav.referrals", "Referrals") },
    { to: "/community", key: "community", label: t("nav.community") },
    { to: "/challenges", key: "challenges", label: t("nav.challenges") },
    { to: "/marketplace", key: "marketplace", label: t("nav.marketplace") },
    { to: "/freelancers", key: "freelancers", label: t("nav.freelancers") },
    { to: "/history", key: "history", label: t("nav.history") },
    { to: "/help", key: "help", label: t("nav.help") },
  ] : [
    { to: "/creator", key: "creator os", label: t("nav.creator") },
    { to: "/help", key: "help", label: t("nav.help") },
  ];
  const links = primary;
  const [moreOpen, setMoreOpen] = useState(false);
  const isHome = useLocation().pathname === "/";

  return (
    <header className={`glass-header ${isHome ? "zx-nav-glow" : ""} sticky top-0 z-50 border-b border-border/60`}>
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-10">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
            <Sparkles className="h-4 w-4 text-primary-foreground" />
          </div>
          <span className={`${isHome ? "zx-logo-glow" : ""} font-display text-xl font-semibold tracking-tight`}>Zuntria</span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.key}`}
              className={({ isActive }) =>
                `rounded-md px-3 py-2 text-sm transition-colors hover:bg-secondary ${isActive ? "text-foreground" : "text-muted-foreground"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          {more.length > 0 && (
            <div className="relative" onMouseLeave={() => setMoreOpen(false)}>
              <button
                onClick={() => setMoreOpen((o) => !o)}
                onMouseEnter={() => setMoreOpen(true)}
                className="rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-secondary"
                data-testid="nav-more"
              >
                {t("nav.more")} ▾
              </button>
              {moreOpen && (
                <div className="absolute right-0 top-full mt-1 w-44 rounded-xl border border-border bg-card p-1 shadow-xl" data-testid="more-dropdown">
                  {more.map((m) => (
                    <NavLink
                      key={m.to}
                      to={m.to}
                      onClick={() => setMoreOpen(false)}
                      data-testid={`nav-more-${m.key}`}
                      className={({ isActive }) =>
                        `block rounded-md px-3 py-2 text-sm transition-colors hover:bg-secondary ${isActive ? "text-foreground" : "text-muted-foreground"}`
                      }
                    >
                      {m.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>

        <div className="flex items-center gap-2">
          {user && (
            <Button variant="ghost" size="icon" onClick={() => setSearchOpen(true)} data-testid="header-search-btn" aria-label="Search">
              <Search className="h-4 w-4" />
            </Button>
          )}
          {user && <NotificationBell />}
          <LanguageSwitcher />
          <Button
            variant="ghost"
            size="icon"
            onClick={toggle}
            data-testid="theme-toggle"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>

          {user ? (
            <div className="hidden items-center gap-2 md:flex">
              {isPremiumUser(user) && (
                <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold tracking-wider text-amber-500" data-testid="pro-badge">PRO</span>
              )}
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary text-sm font-medium" data-testid="user-avatar">
                {user.name?.[0]?.toUpperCase() || "U"}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => { await logout(); nav("/"); }}
                data-testid="logout-btn"
              >
                <LogOut className="mr-1 h-3.5 w-3.5" /> {t("nav.logout")}
              </Button>
            </div>
          ) : (
            <div className="hidden gap-2 md:flex">
              <Button variant="ghost" size="sm" onClick={() => nav("/login")} data-testid="header-login-btn">{t("nav.login")}</Button>
              <Button size="sm" onClick={() => nav("/signup")} data-testid="header-signup-btn">{t("nav.get_started")}</Button>
            </div>
          )}

          <Button
            variant="ghost"
            size="icon"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      <SidebarNav open={open} onClose={() => setOpen(false)} />
      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="top-24 max-w-xl translate-y-0 p-5" data-testid="header-search-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">What do you need right now?</DialogTitle>
          </DialogHeader>
          <SmartSearch onRouted={() => setSearchOpen(false)} />
        </DialogContent>
      </Dialog>
    </header>  );
}
